import { type TrackPoint, type TackStats } from '../types';

function avg<T>(array: T[], pick: (x: T) => number): number {
  if (array.length === 0) {
    return 0;
  }
  let sum = 0;
  for (const element of array) {
    sum += pick(element);
  }
  return sum / array.length;
}

function toMs(utc: string | number | Date): number {
  return new Date(utc).getTime();
}

function windowBefore(points: TrackPoint[], t0ms: number, seconds: number): TrackPoint[] {
  const from = t0ms - seconds * 1000;
  const response: TrackPoint[] = [];
  for (const point of points) {
    const t = toMs(point.utc);
    if (t < t0ms && t >= from) {
      response.push(point);
    }
  }
  return response;
}

function maxRudderAndTime(
  values: TrackPoint[],
  t0ms: number,
): { maxRudder: number; timeToMaxRudder: number } {
  let maxRudder = 0;
  let timeToMaxRudder = 0;
  for (const value of values) {
    const r = value.rudder_abs ?? 0;
    if (r > maxRudder) {
      maxRudder = r;
      timeToMaxRudder = (toMs(value.utc) - t0ms) / 1000;
    }
  }
  return { maxRudder, timeToMaxRudder };
}

function sogMinIndex(values: TrackPoint[]): number {
  let min = Infinity;
  let index = -1;
  for (const [index_, value] of values.entries()) {
    const s = value.sog ?? Infinity;
    if (s < min) {
      min = s;
      index = index_;
    }
  }
  return index;
}

function recoveryTimes(
  values: TrackPoint[],
  startIndex: number,
  target90: number,
  target95: number,
): { timeToRecovery90: number; timeToRecovery95: number } {
  if (startIndex === -1) {
    return { timeToRecovery90: 0, timeToRecovery95: 0 };
  }
  const tStart = toMs(values[startIndex].utc);
  let t90 = 0;
  let t95 = 0;
  let f90 = false;
  let f95 = false;

  for (let index = startIndex; index < values.length; index++) {
    const s = values[index].sog ?? 0;
    const dt = (toMs(values[index].utc) - tStart) / 1000;
    if (!f90 && s >= target90) {
      t90 = dt;
      f90 = true;
    }
    if (!f95 && s >= target95) {
      t95 = dt;
      f95 = true;
    }
    if (f90 && f95) {
      break;
    }
  }
  return { timeToRecovery90: t90, timeToRecovery95: t95 };
}

function tackTypeFromTwa(avgTwaBeforeTack: number): 'tack' | 'gybe' {
  return avgTwaBeforeTack < 90 ? 'tack' : 'gybe';
}

export function calcularEstadisticasTack(
  values: TrackPoint[],
  trackDataCompleto: TrackPoint[],
): TackStats {
  if (!values?.length) {
    return {
      tack: 0,
      maxRudder: 0,
      timeToMaxRudder: 0,
      avgSogBeforeTack: 0,
      avgTwaBeforeTack: 0,
      SogMin: 0,
      timeToRecovery90: 0,
      timeToRecovery95: 0,
      type: 'tack',
    };
  }

  const SEGUNDOS_ANTES_TACK = 5;
  const t0ms = toMs(values[0].utc);

  const previous = windowBefore(trackDataCompleto, t0ms, SEGUNDOS_ANTES_TACK);
  const avgSogBeforeTack = avg(previous, (p) => p.sog ?? 0);
  const avgTwaBeforeTack = avg(previous, (p) => Math.abs(p.twa ?? 0));

  const { maxRudder, timeToMaxRudder } = maxRudderAndTime(values, t0ms);

  const indexMin = sogMinIndex(values);
  const sogMinValue = indexMin === -1 ? 0 : (values[indexMin].sog ?? 0);

  const vel90 = 0.9 * avgSogBeforeTack;
  const vel95 = 0.95 * avgSogBeforeTack;
  const { timeToRecovery90, timeToRecovery95 } = recoveryTimes(values, indexMin, vel90, vel95);

  const tackType = tackTypeFromTwa(avgTwaBeforeTack);

  return {
    tack: 0, // Asignado posteriormente en el mapping
    maxRudder,
    timeToMaxRudder,
    avgSogBeforeTack,
    avgTwaBeforeTack,
    SogMin: Number.isFinite(sogMinValue) ? sogMinValue : 0,
    timeToRecovery90,
    timeToRecovery95,
    type: tackType,
  };
}
